/* ───────────────────────────────────────────────────────────────
   Agent Console — design tokens (ported from the design handoff
   ac-theme.jsx). makeTokens() returns a flat map of CSS custom
   properties applied to the .ac-root container; components read
   them via var(--token) in styles/agent-console.css.

   House style (per the redesign decision): the layout *direction* is
   fixed to 'aurora' and the accent to the BotableX brand blue — only
   THEME (light/dark) and LANGUAGE (en/he) are user-switchable. The
   function still accepts direction/accent so the token layer stays
   reusable if that decision is revisited.
   ─────────────────────────────────────────────────────────────── */

export type ThemeMode = 'light' | 'dark';
export type ThemeDirection = 'aurora' | 'horizon';

/** A flat CSS-custom-property map. Cast to React.CSSProperties at the
 *  call site (React accepts `--x` keys at runtime; the cast satisfies TS). */
export type CssVars = Record<`--${string}`, string>;

export const BRAND = {
  navy: '#0F2E4D',
  blue: '#1E6BD8',
  teal: '#16C2C9',
  grad: 'linear-gradient(135deg, #1E6BD8 0%, #16C2C9 100%)',
  gradSoft: 'linear-gradient(135deg, rgba(30,107,216,0.12), rgba(22,194,201,0.12))',
} as const;

/** Default house accent — not user-switchable in this build. */
export const DEFAULT_ACCENT = BRAND.blue;
export const DEFAULT_DIRECTION: ThemeDirection = 'aurora';

// Status palette shared across directions/themes.
const STATUS = {
  success: '#16A86B',
  successSoft: 'rgba(22,168,107,0.12)',
  warning: '#E8923B',
  warningSoft: 'rgba(232,146,59,0.14)',
  danger: '#E05260',
  dangerSoft: 'rgba(224,82,96,0.13)',
  info: '#3B82C4',
  infoSoft: 'rgba(59,130,196,0.13)',
  consult: '#8B5CF6', // bot consultation accent (violet)
  consultSoft: 'rgba(139,92,246,0.13)',
} as const;

/**
 * Chat timeline + answer-bar semantics (console v2, D14). Deliberately NOT the same colours as StateBadge:
 * `STATUS.consult` (violet) stays the "bot asked for help" badge, while a consultation MESSAGE and the
 * guidance answer bar are red - "internal, the customer never sees this". customer = green, bot = light blue.
 * Built from STATUS so the two palettes cannot drift apart by hand-editing one hex.
 */
export const CHAT_PALETTE = {
  customer: STATUS.success,
  bot: STATUS.info,
  consult: STATUS.danger,
  /**
   * 2026-10-28 (operator): an agent's guidance TO the bot used to be the same red as the bot's own consultation note,
   * so a reader could not tell who was asking whom. Guidance is deep purple; the bot's note stays red. (It was raspberry
   * #C2185B first; the operator found that too close to the red note on screen, so it moved ~70 degrees of hue away.) The guidance
   * answer bar and its send button take the same purple, so what is being typed and the bubble it becomes match.
   * Deep purple on white is 8.2:1 (AA). It is too dark for TEXT on the dark page (2.4:1), so text uses
   * `--chat-guidance-ink`, which is this colour in light mode and a lighter purple in dark mode.
   */
  guidance: '#7B1FA2',
  answerCustomer: STATUS.success,
  answerGuidance: '#7B1FA2',
} as const;

/** Text in the guidance colour on the DARK page: 8.1:1 on #080D16 (the deep purple itself is 2.4:1 there). */
export const GUIDANCE_INK_DARK = '#CE93D8';

export { STATUS };

export interface MakeTokensArgs {
  direction?: ThemeDirection;
  mode: ThemeMode;
  accent?: string;
}

export function makeTokens({ direction = DEFAULT_DIRECTION, mode, accent }: MakeTokensArgs): CssVars {
  const acc = accent || BRAND.blue;
  const accGrad = `linear-gradient(135deg, ${acc} 0%, ${BRAND.teal} 100%)`;
  const dark = mode === 'dark';
  const horizon = direction === 'horizon';

  // ── Neutral surfaces ──────────────────────────────────────────
  const light = {
    bg: horizon ? '#EEF2F7' : '#F6F8FC',
    surface: '#FFFFFF',
    surface2: horizon ? '#F4F7FB' : '#F1F5FA',
    surface3: '#E9EFF6',
    border: horizon ? '#DCE3EC' : '#E7ECF3',
    borderStrong: '#CBD5E1',
    text: '#0F1B2D',
    textMuted: '#5B6B82',
    textFaint: '#93A1B5',
    railBg: horizon
      ? 'linear-gradient(180deg, #0F2E4D 0%, #0C3A55 60%, #0E4A57 100%)'
      : '#FFFFFF',
    railText: horizon ? 'rgba(255,255,255,0.72)' : '#5B6B82',
    railTextActive: horizon ? '#FFFFFF' : acc,
    railActiveBg: horizon ? 'rgba(255,255,255,0.14)' : 'rgba(30,107,216,0.10)',
    railBorder: horizon ? 'rgba(255,255,255,0.10)' : '#E7ECF3',
    railLogoText: horizon ? '#FFFFFF' : '#0F1B2D',
    topbarBg: 'rgba(255,255,255,0.85)',
    shadowSm: '0 1px 2px rgba(15,27,45,0.05)',
    shadowMd: horizon ? '0 2px 8px rgba(15,27,45,0.07)' : '0 4px 16px -4px rgba(15,27,45,0.10)',
    shadowLg: horizon ? '0 8px 28px -8px rgba(15,27,45,0.16)' : '0 18px 50px -16px rgba(15,27,45,0.20)',
    bubbleCustomer: '#F1F5FA',
    bubbleCustomerText: '#0F1B2D',
    chatCustomerSoft: 'rgba(22,168,107,0.12)',
    chatBotSoft: 'rgba(59,130,196,0.13)',
    chatConsultSoft: 'rgba(224,82,96,0.10)',
    chatGuidanceSoft: 'rgba(123,31,162,0.14)',
    chatGuidanceInk: CHAT_PALETTE.guidance,
    tipBg: '#0F2E4D',
    tipText: '#FFFFFF',
  };

  const darkT = {
    bg: '#080D16',
    surface: horizon ? '#0F1825' : '#121A26',
    surface2: '#19222F',
    surface3: '#212C3B',
    border: '#243042',
    borderStrong: '#33425A',
    text: '#E8EEF6',
    textMuted: '#94A6BE',
    textFaint: '#62748E',
    railBg: horizon
      ? 'linear-gradient(180deg, #0A2238 0%, #0A2E40 60%, #0B3A44 100%)'
      : '#0C1420',
    railText: 'rgba(233,239,246,0.66)',
    railTextActive: '#FFFFFF',
    railActiveBg: horizon ? 'rgba(255,255,255,0.12)' : 'rgba(30,107,216,0.22)',
    railBorder: 'rgba(255,255,255,0.07)',
    railLogoText: '#FFFFFF',
    topbarBg: 'rgba(12,20,32,0.82)',
    shadowSm: '0 1px 2px rgba(0,0,0,0.4)',
    shadowMd: '0 4px 16px -4px rgba(0,0,0,0.5)',
    shadowLg: '0 18px 50px -16px rgba(0,0,0,0.6)',
    bubbleCustomer: '#1B2532',
    bubbleCustomerText: '#E8EEF6',
    chatCustomerSoft: 'rgba(22,168,107,0.20)',
    chatBotSoft: 'rgba(59,130,196,0.22)',
    chatConsultSoft: 'rgba(224,82,96,0.18)',
    chatGuidanceSoft: 'rgba(123,31,162,0.30)',
    chatGuidanceInk: GUIDANCE_INK_DARK,
    tipBg: '#E5ECF4',
    tipText: '#0F2E4D',
  };

  const t = dark ? darkT : light;

  // ── Shape & density per direction ─────────────────────────────
  const shape = horizon
    ? { radius: '11px', radiusSm: '8px', radiusLg: '16px', radiusPill: '8px' }
    : { radius: '16px', radiusSm: '11px', radiusLg: '22px', radiusPill: '999px' };

  return {
    '--bg': t.bg,
    '--surface': t.surface,
    '--surface-2': t.surface2,
    '--surface-3': t.surface3,
    '--border': t.border,
    '--border-strong': t.borderStrong,
    '--text': t.text,
    '--text-muted': t.textMuted,
    '--text-faint': t.textFaint,

    '--accent': acc,
    '--accent-2': BRAND.teal,
    '--accent-grad': accGrad,
    '--accent-soft': dark ? 'rgba(30,107,216,0.20)' : 'rgba(30,107,216,0.10)',
    '--accent-contrast': '#FFFFFF',

    '--rail-bg': t.railBg,
    '--rail-text': t.railText,
    '--rail-text-active': t.railTextActive,
    '--rail-active-bg': t.railActiveBg,
    '--rail-border': t.railBorder,
    '--rail-logo-text': t.railLogoText,
    '--topbar-bg': t.topbarBg,

    '--radius': shape.radius,
    '--radius-sm': shape.radiusSm,
    '--radius-lg': shape.radiusLg,
    '--radius-pill': shape.radiusPill,

    '--shadow-sm': t.shadowSm,
    '--shadow-md': t.shadowMd,
    '--shadow-lg': t.shadowLg,

    '--bubble-customer': t.bubbleCustomer,
    '--bubble-customer-text': t.bubbleCustomerText,

    '--success': STATUS.success, '--success-soft': STATUS.successSoft,
    '--warning': STATUS.warning, '--warning-soft': STATUS.warningSoft,
    '--danger': STATUS.danger, '--danger-soft': STATUS.dangerSoft,
    '--info': STATUS.info, '--info-soft': STATUS.infoSoft,
    '--consult': STATUS.consult, '--consult-soft': STATUS.consultSoft,

    // Console v2 chat / answer-bar semantics (see CHAT_PALETTE). Soft variants are theme-aware.
    '--chat-customer': CHAT_PALETTE.customer, '--chat-customer-soft': t.chatCustomerSoft,
    '--chat-bot': CHAT_PALETTE.bot, '--chat-bot-soft': t.chatBotSoft,
    '--chat-consult': CHAT_PALETTE.consult, '--chat-consult-soft': t.chatConsultSoft,
    '--chat-guidance': CHAT_PALETTE.guidance, '--chat-guidance-soft': t.chatGuidanceSoft,
    '--chat-guidance-ink': t.chatGuidanceInk,
    '--answer-guidance': CHAT_PALETTE.answerGuidance,
    '--answer-customer': CHAT_PALETTE.answerCustomer,
    '--tip-bg': t.tipBg, '--tip-text': t.tipText,

    '--brand-grad': BRAND.grad,
    '--brand-navy': BRAND.navy,

    '--pad': horizon ? '18px' : '22px',
    '--gap': horizon ? '14px' : '18px',
    '--font': "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
    '--font-he': "'Heebo', 'Plus Jakarta Sans', system-ui, sans-serif",
  };
}
